import React, { useEffect, useState } from 'react';
import { useSEO } from '../lib/useSEO';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { sprintAPI } from '../lib/api';
import { CheckCircle, XCircle, ArrowLeft, RotateCcw, Trophy, BarChart3 } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_trivia-saas/artifacts/9y2lje2h_Jep_Prep_Logo.png';

export default function SprintResults() {
  const { sessionId } = useParams();
  useSEO('Sprint Results | JepPrep', 'Review your sprint performance, accuracy, and speed stats.');
  const navigate = useNavigate();
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadResults();
  }, [sessionId]);

  const loadResults = async () => {
    try {
      const response = await sprintAPI.getResults(sessionId);
      setResults(response.data);
    } catch (error) {
      console.error('Failed to load results:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Loading results...</div>
      </div>
    );
  }

  if (!results) {
    return (
      <div className="min-h-screen bg-[hsl(var(--background))] flex items-center justify-center">
        <div className="text-gray-400">Results not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      {/* Navigation */}
      <nav className="border-b border-[hsl(var(--border))] bg-[hsl(var(--secondary))]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <div className="flex items-center gap-2 sm:gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="text-gray-300 shrink-0">
                  <ArrowLeft className="w-4 h-4" />
                </Button>
              </Link>
              <img src={LOGO_URL} alt="JepPrep" className="h-12 shrink-0" />
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Score Summary */}
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] mb-6 sm:mb-8">
          <CardContent className="p-5 sm:p-8 text-center">
            <Trophy className="w-12 h-12 sm:w-16 sm:h-16 mx-auto text-[hsl(var(--brand-gold-500))] mb-3 sm:mb-4" />
            <h1 className="font-display font-bold text-2xl sm:text-4xl text-white mb-2">Sprint Complete!</h1>
            <div className="text-4xl sm:text-6xl font-mono font-bold text-[hsl(var(--brand-gold-500))] mb-3 sm:mb-4" data-testid="sprint-accuracy">
              {results.accuracy}%
            </div>
            <div className="flex justify-center gap-4 sm:gap-8 text-sm sm:text-lg mb-6 sm:mb-8">
              <div className="text-green-500" data-testid="sprint-correct">
                <CheckCircle className="w-5 h-5 sm:w-6 sm:h-6 inline mr-1 sm:mr-2" />
                {results.correct_count} Correct
              </div>
              <div className="text-red-500" data-testid="sprint-incorrect">
                <XCircle className="w-5 h-5 sm:w-6 sm:h-6 inline mr-1 sm:mr-2" />
                {results.incorrect_count} Incorrect
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
              <Button 
                onClick={() => navigate('/sprint')}
                className="bg-[hsl(var(--brand-gold-500))] text-[hsl(var(--brand-blue-900))] hover:bg-[hsl(var(--brand-blue-800))] hover:text-white px-6 py-5 text-base"
                data-testid="play-again-button"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                Play Again
              </Button>
              <Button 
                onClick={() => navigate('/sprint/stats')}
                variant="outline"
                className="border-[hsl(var(--border))] text-white hover:bg-[hsl(var(--accent))] px-6 py-5 text-base"
                data-testid="sprint-statistics-button"
              >
                <BarChart3 className="w-5 h-5 mr-2" />
                Sprint Statistics
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Category Breakdown */}
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))] mb-6 sm:mb-8">
          <CardHeader>
            <CardTitle className="text-white">Category Performance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(results.category_breakdown || {}).map(([category, stats]) => {
                const accuracy = stats.total > 0 ? Math.round((stats.correct / stats.total) * 100) : 0;
                return (
                  <div key={category} className="space-y-2">
                    <div className="flex justify-between text-sm gap-2">
                      <span className="text-gray-300 truncate min-w-0">{category}</span>
                      <span className="text-white font-mono shrink-0">{stats.correct}/{stats.total} ({accuracy}%)</span>
                    </div>
                    <div className="h-2 bg-[hsl(var(--accent))] rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[hsl(var(--brand-gold-500))] transition-all"
                        style={{ width: `${accuracy}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Question Review */}
        <Card className="bg-[hsl(var(--secondary))] border-[hsl(var(--border))]">
          <CardHeader>
            <CardTitle className="text-white">Question Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {results.questions?.map((q, idx) => (
              <div key={q.question || `q-${idx}`} className={`p-3 sm:p-4 rounded-lg border ${q.is_correct ? 'bg-green-500/10 border-green-500/30' : 'bg-red-500/10 border-red-500/30'}`}>
                <div className="flex items-start gap-2 sm:gap-3">
                  {q.is_correct ? (
                    <CheckCircle className="w-5 h-5 text-green-500 mt-1 flex-shrink-0" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-[hsl(var(--brand-gold-400))] text-xs sm:text-sm mb-1">{q.category}</p>
                    <p className="text-white text-sm sm:text-base mb-2">{q.question}</p>
                    <div className="text-xs sm:text-sm">
                      <p className="text-green-400 break-words">Correct: {q.correct_answer}</p>
                      {!q.is_correct && q.player_answer && (
                        <p className="text-red-400 break-words">Your answer: {q.player_answer}</p>
                      )}
                      {!q.is_correct && !q.player_answer && (
                        <p className="text-gray-500">No answer (timeout)</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
